import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Header as CoreHeader,
  Icon,
  Modal,
  UserMenu,
  useSidebar,
} from '@hapvida/hapvida-core-components';

import HapvidaNotredameColorsMobile from '@hapvida/hapvida-core-components/src/assets/images/hapvida-ndi-logo-colors-mobile.svg';
import HapvidaNotredameLightMobile from '@hapvida/hapvida-core-components/src/assets/images/hapvida-ndi-logo-light-mobile.svg';

import menuIconDefault from '@assets/images/menu-icon-default.svg';
import menuIcon from '@assets/images/menu-icon.svg';
import { UserType } from '@contexts';
import { BackofficeUserListRouter } from '@flows/private/backofficeUser/pages/BackofficeUserList/router';
import { CompanySetupListRouter } from '@flows/private/companySetup/pages/CompanySetupList/router';
import { useAuth } from '@hooks';

import { SelectedCompany, SelectedPartnerCompany } from './components';
import {
  BackButtonMobile,
  ButtonHomeLogo,
  CompanySelectedInfo,
  HeaderMobileColored,
  MenuButton,
  MenuButtonMobile,
  MobileHiddenStack,
} from './styles';
import type { HeaderProps } from './types';

export function Header({
  mainContent,
  theme: themeDefault,
  showMenu = true,
  onGoBack,
  showBackButton = true,
  showSubHeader = true,
}: Readonly<HeaderProps>) {
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const { user, logOut, selectedCompany, selectedPartnerCompany } = useAuth();
  const isLoggedUser = Boolean(user);
  const { toggleDrawer } = useSidebar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const currentPath = window.location.pathname;

  const routeIncludesBackofficeUsers = Boolean(
    currentPath?.includes(BackofficeUserListRouter.path),
  );

  const routeIncludesCompanySetupList = Boolean(
    currentPath?.includes(CompanySetupListRouter.path),
  );

  const isBackoffice = user?.companyType === UserType.Backoffice;
  const isThemeDark = themeDefault ? themeDefault === 'dark' : isBackoffice;
  const shouldShowBackButton = showBackButton && !routeIncludesCompanySetupList;

  const emptySelected = !selectedCompany || !selectedPartnerCompany;

  const showHeaderContent = !routeIncludesBackofficeUsers;

  const hapvidaLogoConditionBackoffice = isThemeDark
    ? HapvidaNotredameLightMobile
    : HapvidaNotredameColorsMobile;

  const handleOnClickBackButton = () => {
    return onGoBack?.() || navigate(-1);
  };

  const renderFallbackDiv = () => {
    return <Box width={'24px'} height={'24px'} />;
  };

  const renderSelectedCompanyComponent = () => {
    return (
      !emptySelected &&
      showSubHeader && (
        <CompanySelectedInfo
          alignItems="center"
          justifyContent="center"
          gap={4}
        >
          <SelectedCompany />
          <SelectedPartnerCompany />
        </CompanySelectedInfo>
      )
    );
  };

  return (
    <>
      {isMobile ? (
        <CoreHeader
          mainContent={mainContent}
          isBackofficeUser={isThemeDark}
          isMobile={isMobile}
          selectedCompanyComponent={renderSelectedCompanyComponent()}
        >
          <HeaderMobileColored isBackofficeUser={isThemeDark}>
            {shouldShowBackButton ? (
              <BackButtonMobile
                onClick={handleOnClickBackButton}
                startIcon={<Icon name="actions-b2b-chevron-left" size={24} />}
                isBackofficeUser={isThemeDark}
                type="button"
              />
            ) : (
              renderFallbackDiv()
            )}
            <Box flexDirection="row" gap={2} justifySelf={'center'}>
              <ButtonHomeLogo
                onClick={isLoggedUser ? () => navigate('/home') : undefined}
                type="button"
                isLoggedUser={isLoggedUser}
              >
                <img
                  src={hapvidaLogoConditionBackoffice}
                  alt="Hapvida"
                  height={24}
                />
              </ButtonHomeLogo>
            </Box>
            {showMenu ? (
              <MenuButtonMobile
                startIcon={
                  <img
                    src={isThemeDark ? menuIcon : menuIconDefault}
                    alt="menu icon"
                    height={12}
                  />
                }
                onClick={toggleDrawer}
                type="button"
              />
            ) : (
              renderFallbackDiv()
            )}
          </HeaderMobileColored>
        </CoreHeader>
      ) : (
        <CoreHeader mainContent={mainContent}>
          <Stack flex={1}>
            <Grid display={{ sm: 'none' }}>
              <MenuButton
                variant="text"
                startIcon={<Icon name="actions-b2b-menu" />}
                onClick={toggleDrawer}
              >
                Menu
              </MenuButton>
            </Grid>
            {showHeaderContent && (
              <MobileHiddenStack>
                <SelectedCompany />
                <SelectedPartnerCompany />
              </MobileHiddenStack>
            )}
          </Stack>
          <Stack>
            <UserMenu
              username={user?.name}
              onLogOut={() => setShowLogoutConfirmation(true)}
            />
          </Stack>
        </CoreHeader>
      )}

      <Modal
        icon="actions-b2b-alert-octagon-filled"
        secondaryButtonText="Fechar"
        mainButtonText="Sair mesmo assim"
        title="Atenção! As informações não finalizadas poderão ser perdidas."
        mainAction={logOut}
        open={showLogoutConfirmation}
        handleCloseModal={() => setShowLogoutConfirmation(false)}
      >
        <Typography variant="body1" fontSize={14}>
          Tem certeza que desejar sair?
        </Typography>
        <Typography variant="body1" fontSize={14}>
          Ao sair, informações não finalizadas poderão ser perdidas.
        </Typography>
      </Modal>
    </>
  );
}
